import * as SelectPrimitive from '@radix-ui/react-select';
import type * as React from 'react';
import { cn } from '@/shared/lib/utils';
import { Input } from '@/shared/ui/form/input';
import { Separator } from '../../../separator/Separator';
import type { SelectOption } from '../helpers/types';
import { normalizeOptionValue } from '../helpers/utils';
import { SelectItem } from './SelectItem';

/**
 * SelectContent - ドロップダウンコンテンツ
 */
type SelectContentProps = React.ComponentPropsWithRef<typeof SelectPrimitive.Content> & {
  /** 検索機能を有効にする */
  showSearch?: boolean;
  /** 検索値 */
  searchValue?: string;
  /** 検索値変更ハンドラ */
  onSearchChange?: (value: string) => void;
  /** 固定された下部オプション */
  fixedBottomOptions?: SelectOption[] | undefined;
};

export const SelectContent = ({
  className,
  children,
  avoidCollisions = true,
  ref,
  showSearch = false,
  searchValue = '',
  onSearchChange,
  fixedBottomOptions,
  ...props
}: SelectContentProps) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      avoidCollisions={avoidCollisions}
      className={cn(
        'relative z-50 max-h-96 min-w-[8rem]',
        'overflow-hidden rounded-md shadow-sm',
        'bg-white shadow-md',
        'data-[state=closed]:animate-out data-[state=open]:animate-in',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
        'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        'data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=bottom]:translate-y-1 data-[side=top]:-translate-y-1',
        className,
      )}
      position={'popper'}
      {...props}
    >
      {showSearch && (
        <div className="p-1">
          <Input
            type="text"
            value={searchValue}
            onChange={(e) => onSearchChange?.(e.target.value)}
            placeholder="検索..."
            autoComplete={false}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          />
        </div>
      )}
      <SelectPrimitive.Viewport
        className={cn(
          'p-1',
          'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]',
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      {fixedBottomOptions && (
        <div>
          <Separator />
          <div className="p-1">
            {fixedBottomOptions.map((option) => (
              <SelectItem
                key={normalizeOptionValue(option.value)}
                value={normalizeOptionValue(option.value)}
              >
                {option.label}
              </SelectItem>
            ))}
          </div>
        </div>
      )}
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
);
SelectContent.displayName = SelectPrimitive.Content.displayName;
